import type { ReactNode } from "react";
import { FilterPills } from "./FilterPills";
import type { FilterPillSpec } from "./FilterPills";

type CommonHeaderProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  filterPills?: FilterPillSpec[];
  headerExtra?: ReactNode;
};

// Used inside .chart-tile (HBarChart, VBarChart, etc.).
export function ChartCardHeader({ title, subtitle, filterPills, headerExtra }: CommonHeaderProps) {
  const hasPills = !!filterPills && filterPills.length > 0;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: hasPills ? 8 : 0 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h3 style={{
            fontSize: 13, fontWeight: 600, color: "var(--fg-1)", margin: 0,
            display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
          }}>
            <span>{title}</span>
          </h3>
          {subtitle && (
            <div style={{ fontSize: 11.5, color: "var(--fg-3)", marginTop: 2 }}>{subtitle}</div>
          )}
        </div>
        {headerExtra && <div style={{ flexShrink: 0 }}>{headerExtra}</div>}
      </div>
      {hasPills && <FilterPills pills={filterPills!} />}
    </div>
  );
}

// Wrapper for .chart-tile chart bodies. Replaces the manual <h3> + sub
// pattern from the legacy chart primitives.
export function ChartCard({
  title, subtitle, filterPills, headerExtra, children,
}: CommonHeaderProps & { children: ReactNode }) {
  return (
    <div className="chart-tile">
      <ChartCardHeader
        title={title}
        subtitle={subtitle}
        filterPills={filterPills}
        headerExtra={headerExtra}
      />
      {children}
    </div>
  );
}

// Used inside .card-h headers (the heavier card pattern with a body
// underneath, like CollapsiblePanel children or the mention list/cloud
// panel). Renders title + subtitle stacked, pills row beneath, headerExtra
// to the right.
export function SectionCardHeader({
  title, subtitle, filterPills, headerExtra, noBorder,
}: CommonHeaderProps & { noBorder?: boolean }) {
  const hasPills = !!filterPills && filterPills.length > 0;
  return (
    <div className={`card-h${noBorder ? " no-border" : ""}`} style={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 6 }}>
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, width: "100%" }}>
        <div className="title-wrap" style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ margin: 0 }}>{title}</h2>
          {subtitle && <div className="sub">{subtitle}</div>}
        </div>
        {headerExtra && <div style={{ flexShrink: 0 }}>{headerExtra}</div>}
      </div>
      {hasPills && <FilterPills pills={filterPills!} />}
    </div>
  );
}
